import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { message } from "antd";
import { LoadingStatusCode } from "../../../../lib/types/LoadingStatusCode";
import { catchApiError } from "../../../../lib/utils/catchApiError";
import {
  IOfferingGroupsAddOfferSearch,
  IOfferingGroupsSearch,
} from "../../types/IOfferingGroupsSearch";
import {
  addedOfferingGroupMemberRoute,
  addOfferingForGroupRoute,
  createOfferingGroupRoute,
  deleteOfferingGroupMemberRoute,
  fetchOfferingGroupIdRoute,
  fetchOfferingGroupsPolicyRoute,
  fetchOfferingGroupsVersionsRoute,
  getOfferingGroupsDicRoute,
  offeringGroupsSearchRoute,
  updateOfferingGroupCloseDateRoute,
  updateOfferingGroupMemberCloseDateRoute,
  updateOfferingGroupRoute,
} from "./offeringGroupsRoutes";
import { DictionaryDto } from "../../../../lib/interfaces";
import versionsTableUtils from "../../../../lib/utils/versionsTableUtils";
import {
  IAddOfferingForGroup,
  IOfferingGroupCreate,
  IOfferingGroup,
  IOfferGroupFetchParams,
} from "../../types/IOfferingGroup";
import { IOfferingGroupUpdate } from "../../types/IOfferingGroupCreate";
import { IPolicy } from "../../types/IPolicy";

interface InitialState {
  offeringGroups: IOfferingGroup[];
  offeringGroupsPagination: {
    current: number;
    total: number;
  };
  offeringGroupsLoading: LoadingStatusCode;
  offeringGroupsTotalElements: number;
  offeringGroupsTypesDic: DictionaryDto[];
  offeringGroupsTypesDicLoading: LoadingStatusCode;
  offeringGroupsEntityObjectTypeDic: DictionaryDto[];
  offeringGroupsEntityObjectTypeDicLoading: LoadingStatusCode;
  offeringGroupsVersions: IOfferingGroup[];
  offeringGroupsVersionsLoading: LoadingStatusCode;
  offeringGroup?: IOfferingGroup;
  offeringGroupLoading: LoadingStatusCode;
  offeringGroupsPolicy: IPolicy[];
  offeringGroupsPolicyLoading: LoadingStatusCode;
  addedOfferingGroupMemberLoading: LoadingStatusCode;
  updateOfferingGroupMemberCloseDateLoading: LoadingStatusCode;
  updateOfferingGroupLoading: LoadingStatusCode;
  updateOfferingGroupCloseDateLoading: LoadingStatusCode;
  createOfferingGroupLoading: LoadingStatusCode;
  offeringGroupsAddOffer: IOfferingGroup[];
  offeringGroupsAddOfferPagination: {
    current: number;
    total: number;
  };
  offeringGroupsAddOfferLoading: LoadingStatusCode;
  offeringGroupsAddOfferTotalElements: number;
  addOfferingForGroupLoading: LoadingStatusCode;
}

const initialState: InitialState = {
  offeringGroups: [],
  offeringGroupsPagination: {
    current: 0,
    total: 0,
  },
  offeringGroupsLoading: "idle",
  offeringGroupsTotalElements: 0,
  offeringGroupsTypesDic: [],
  offeringGroupsTypesDicLoading: "idle",
  offeringGroupsEntityObjectTypeDic: [],
  offeringGroupsEntityObjectTypeDicLoading: "idle",
  offeringGroupsVersions: [],
  offeringGroupsVersionsLoading: "idle",
  offeringGroupLoading: "idle",
  offeringGroupsPolicy: [],
  offeringGroupsPolicyLoading: "idle",
  addedOfferingGroupMemberLoading: "idle",
  updateOfferingGroupMemberCloseDateLoading: "idle",
  updateOfferingGroupLoading: "idle",
  updateOfferingGroupCloseDateLoading: "idle",
  createOfferingGroupLoading: "idle",
  offeringGroupsAddOffer: [],
  offeringGroupsAddOfferPagination: {
    current: 0,
    total: 0,
  },
  offeringGroupsAddOfferLoading: "idle",
  offeringGroupsAddOfferTotalElements: 0,
  addOfferingForGroupLoading: "idle",
};

// Поиск групп предложений
export const searchOfferingGroups = createAsyncThunk(
  "offeringGroups/searchOfferingGroups",
  async (searchParams: IOfferingGroupsSearch<string>) => {
    try {
      const response = axios.get(offeringGroupsSearchRoute(searchParams));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка поиска групп. Попробуйте позже",
      });
    }
  }
);

// Получение справочника типов групп
export const fetchOfferingGroupsTypesDic = createAsyncThunk(
  "offeringGroups/fetchOfferingGroupsTypesDic",
  async () => {
    try {
      const response = axios.get(getOfferingGroupsDicRoute("group_type"));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки типов групп. Попробуйте позже",
      });
    }
  }
);

// Получение версий групп
export const fetchOfferingGroupsVersions = createAsyncThunk(
  "offeringGroups/fetchOfferingGroupsVersions",
  async (offeringGroupId: number) => {
    try {
      const response = axios.get(fetchOfferingGroupsVersionsRoute(offeringGroupId));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки версий групп предложений. Попробуйте позже",
      });
    }
  }
);

// Получение группы по id
export const fetchOfferingGroupId = createAsyncThunk<
  IOfferingGroup,
  { offeringGroupId: number; searchParams?: IOfferGroupFetchParams }
>("offeringGroups/fetchOfferingGroupId", async (params) => {
  try {
    const response = axios.get(fetchOfferingGroupIdRoute(params));
    const { data } = await response;
    return data;
  } catch (e) {
    return catchApiError({
      e,
      title: "Ошибка загрузки группы предложений. Попробуйте позже",
    });
  }
});

// Получение группы по id для добавления в таблицу элементов
export const fetchOfferingGroupIdForTableTree = createAsyncThunk<
  IOfferingGroup,
  { offeringGroupId: number; searchParams?: IOfferGroupFetchParams }
>("offeringGroups/fetchOfferingGroupIdForTableTree", async (params) => {
  try {
    const response = axios.get(fetchOfferingGroupIdRoute(params));
    const { data } = await response;
    return data;
  } catch (e) {
    return catchApiError({
      e,
      title: "Ошибка загрузки группы предложений. Попробуйте позже",
    });
  }
});

// Создание группы
export const createOfferingGroup = createAsyncThunk(
  "offeringGroups/createOfferingGroup",
  async (params: IOfferingGroupCreate) => {
    try {
      message.loading("Создание группы");
      const response = axios.post(createOfferingGroupRoute(), params);
      const { data } = await response;
      message.destroy();
      message.success("Группа успешно создана");
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки группы предложений. Попробуйте позже",
      });
    }
  }
);

// Изменение группы
export const updateOfferingGroup = createAsyncThunk(
  "offeringGroups/updateOfferingGroup",
  async ({ params, body }: IOfferingGroupUpdate, { dispatch }) => {
    try {
      await axios.patch(updateOfferingGroupRoute(params), body);
      message.success("Группа успешно изменена");

      return Promise.all([
        dispatch(fetchOfferingGroupId({ offeringGroupId: params.offeringGroupId })),
        dispatch(fetchOfferingGroupsVersions(params.offeringGroupId)),
      ]);
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка изменения группы предложений. Попробуйте позже",
      });
    }
  }
);

// Изменение даты закрытия группы
export const updateOfferingGroupCloseDate = createAsyncThunk(
  "policies/updateOfferingGroupCloseDate",
  async (
    params: {
      offeringGroupId: number;
      onDate?: string;
    },
    { dispatch }
  ) => {
    try {
      const response = axios.delete(updateOfferingGroupCloseDateRoute(params));

      const { data } = await response;

      message.success("Дата закрытия успешно обновлена");

      Promise.all([
        dispatch(fetchOfferingGroupId({ offeringGroupId: params.offeringGroupId })),
        dispatch(fetchOfferingGroupsVersions(params.offeringGroupId)),
      ]);

      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка обновления даты закрытия. Попробуйте позже",
      });
    }
  }
);

// Получение справочника энтити
export const fetchOfferingGroupsEntityObjectTypeDic = createAsyncThunk(
  "policies/fetchOfferingGroupsEntityObjectTypeDic",
  async () => {
    try {
      const response = axios.get(getOfferingGroupsDicRoute("entity_object_type"));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки списка типов объекта. Попробуйте позже",
      });
    }
  }
);

// Изменение даты закрытия группы
export const updateOfferingGroupMemberCloseDate = createAsyncThunk(
  "policies/updateOfferingGroupMemberCloseDate",
  async (
    params: {
      offeringGroupId: number;
      memberId: number;
      onDate?: string;
    },
    { dispatch }
  ) => {
    try {
      const response = axios.delete(updateOfferingGroupMemberCloseDateRoute(params));

      const { data } = await response;

      message.success("Дата закрытия успешно обновлена");

      Promise.all([dispatch(fetchOfferingGroupId({ offeringGroupId: params.offeringGroupId }))]);

      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка обновления даты закрытия. Попробуйте позже",
      });
    }
  }
);

// Удаление элемента группы
export const deleteOfferingGroupMember = createAsyncThunk(
  "policies/deleteOfferingGroupMember",
  async (
    params: {
      offeringGroupId: number;
      memberId: number;
      onDate?: string;
    },
    { dispatch }
  ) => {
    try {
      await axios.delete(deleteOfferingGroupMemberRoute(params));

      message.success("Элемент группы успешно удалён");

      return Promise.all([
        dispatch(fetchOfferingGroupId({ offeringGroupId: params.offeringGroupId })),
      ]);
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка удаления элемента группы. Попробуйте позже",
      });
    }
  }
);

// Добавление элемента группы
export const addedOfferingGroupMember = createAsyncThunk(
  "policies/addedOfferingGroupMember",
  async (
    params: {
      offeringGroupId: number;
      offeringGroupMembers: {
        memberTypeId: string;
        memberObjectId: number;
        fromDate: string;
        toDate: string;
        inversion: boolean;
      }[];
    },
    { dispatch }
  ) => {
    try {
      const response = axios.post(addedOfferingGroupMemberRoute(), params);

      const { data } = await response;

      message.success("Элемент группы успешно добавлен");

      Promise.all([dispatch(fetchOfferingGroupId({ offeringGroupId: params.offeringGroupId }))]);

      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка добавления элемента группы. Попробуйте позже",
      });
    }
  }
);

// Получение списка политик для группы
export const fetchOfferingGroupsPolicy = createAsyncThunk(
  "policies/fetchOfferingGroupsPolicy",
  async (offeringGroupId: number) => {
    try {
      const response = await Promise.all([
        axios.get<{ content: IPolicy[] }>(
          fetchOfferingGroupsPolicyRoute({
            assocObjectId: offeringGroupId,
            assocTypeId: 5,
          })
        ),
        axios.get<{ content: IPolicy[] }>(
          fetchOfferingGroupsPolicyRoute({
            actorId: offeringGroupId,
            actorTypeId: 5,
          })
        ),
      ]);

      return [response[0].data, response[1].data];
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки списка политик группы. Попробуйте позже",
      });
    }
  }
);

// Поиск групп предложений для добавления предложения в группы
export const searchOfferingGroupsAddOffer = createAsyncThunk(
  "offeringGroups/searchOfferingGroupsAddOffer",
  async (searchParams: Partial<IOfferingGroupsAddOfferSearch<string>>) => {
    try {
      const responseAllGroups = axios.get(offeringGroupsSearchRoute(searchParams.searchAllGroups));

      const responseInqludesGroups = axios.get(
        offeringGroupsSearchRoute(searchParams.searchInqludesGroups)
      );

      const { data: dataAllGroups } = await responseAllGroups;
      const { data: dataInqludesGroups } = await responseInqludesGroups;

      const filteredDataAllGroups = {
        ...dataAllGroups,
        content: dataAllGroups?.content?.filter(
          (allEl) => !dataInqludesGroups?.content?.find((inqludeEl) => inqludeEl.id === allEl.id)
        ),
      };

      return filteredDataAllGroups;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка поиска групп. Попробуйте позже",
      });
    }
  }
);

// Добавление предложения в группу
export const addOfferingForGroup = createAsyncThunk(
  "offeringGroups/addOfferingForGroup",
  async (params: IAddOfferingForGroup) => {
    try {
      message.loading("Добавление предложения");
      const response = axios.post(addOfferingForGroupRoute(params), params);
      const { data } = await response;
      message.destroy();
      message.success("Предложение успешно добавлено");
      return data;
    } catch (e) {
      message.destroy();
      return catchApiError({
        e,
        title: "Ошибка добавления предложения. Попробуйте позже",
      });
    }
  }
);

export const offeringGroupsSlice = createSlice({
  name: "offeringGroups",
  initialState,
  reducers: {
    cleanOfferingGroups: (state) => {
      state.offeringGroupsLoading = "idle";
      state.offeringGroups = [];
      state.offeringGroupsTotalElements = 0;
      state.offeringGroupsPagination = {
        current: 0,
        total: 0,
      };
    },
    cleanOfferingGroupsVersions: (state) => {
      state.offeringGroupsVersionsLoading = "idle";
      state.offeringGroupsVersions = [];
    },
    cleanOfferingGroup: (state) => {
      state.offeringGroup = undefined;
    },
    cleanOfferingAddGroup: (state) => {
      state.offeringGroupsAddOffer = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchOfferingGroups.pending, (state) => {
        state.offeringGroupsLoading = "pending";
      })
      .addCase(searchOfferingGroups.rejected, (state) => {
        state.offeringGroupsLoading = "rejected";
        state.offeringGroups = [];
        state.offeringGroupsTotalElements = 0;
        state.offeringGroupsPagination = {
          current: 0,
          total: 0,
        };
      })
      .addCase(searchOfferingGroups.fulfilled, (state, { payload }) => {
        state.offeringGroupsLoading = "fulfilled";
        state.offeringGroups = payload.content;
        state.offeringGroupsTotalElements = payload.totalElements;
        state.offeringGroupsPagination = {
          current: payload.number,
          total: payload.totalElements,
        };
      })
      .addCase(fetchOfferingGroupsTypesDic.pending, (state) => {
        state.offeringGroupsTypesDicLoading = "pending";
      })
      .addCase(fetchOfferingGroupsTypesDic.rejected, (state) => {
        state.offeringGroupsTypesDicLoading = "rejected";
        state.offeringGroupsTypesDic = [];
      })
      .addCase(fetchOfferingGroupsTypesDic.fulfilled, (state, { payload }) => {
        state.offeringGroupsTypesDicLoading = "fulfilled";
        state.offeringGroupsTypesDic = payload;
      })
      .addCase(fetchOfferingGroupsEntityObjectTypeDic.pending, (state) => {
        state.offeringGroupsEntityObjectTypeDicLoading = "pending";
      })
      .addCase(fetchOfferingGroupsEntityObjectTypeDic.rejected, (state) => {
        state.offeringGroupsEntityObjectTypeDicLoading = "rejected";
        state.offeringGroupsEntityObjectTypeDic = [];
      })
      .addCase(fetchOfferingGroupsEntityObjectTypeDic.fulfilled, (state, { payload }) => {
        state.offeringGroupsEntityObjectTypeDicLoading = "fulfilled";
        state.offeringGroupsEntityObjectTypeDic = payload;
      })
      .addCase(fetchOfferingGroupsVersions.pending, (state) => {
        state.offeringGroupsVersionsLoading = "pending";
      })
      .addCase(fetchOfferingGroupsVersions.rejected, (state) => {
        state.offeringGroupsVersionsLoading = "rejected";
        state.offeringGroupsVersions = [];
      })
      .addCase(fetchOfferingGroupsVersions.fulfilled, (state, { payload }) => {
        state.offeringGroupsVersionsLoading = "fulfilled";
        state.offeringGroupsVersions = payload.sort(versionsTableUtils.sortVersionsByDate);
      })
      .addCase(fetchOfferingGroupId.pending, (state) => {
        state.offeringGroupLoading = "pending";
      })
      .addCase(fetchOfferingGroupId.rejected, (state) => {
        state.offeringGroupLoading = "rejected";
        state.offeringGroup = undefined;
      })
      .addCase(fetchOfferingGroupId.fulfilled, (state, { payload }) => {
        state.offeringGroupLoading = "fulfilled";
        state.offeringGroup = payload;
      })
      .addCase(fetchOfferingGroupsPolicy.pending, (state) => {
        state.offeringGroupsPolicyLoading = "pending";
      })
      .addCase(fetchOfferingGroupsPolicy.rejected, (state) => {
        state.offeringGroupsPolicyLoading = "rejected";
        state.offeringGroupsPolicy = [];
      })
      .addCase(fetchOfferingGroupsPolicy.fulfilled, (state, action) => {
        state.offeringGroupsPolicyLoading = "fulfilled";

        let policyArr: IPolicy[] = [];

        Array.isArray(action.payload) &&
          action.payload.forEach((el) => {
            if (el && Array.isArray(el.content)) {
              policyArr = policyArr.concat(el.content);
            }
          });

        state.offeringGroupsPolicy = policyArr;
      })
      .addCase(addedOfferingGroupMember.pending, (state) => {
        state.addedOfferingGroupMemberLoading = "pending";
      })
      .addCase(addedOfferingGroupMember.rejected, (state) => {
        state.addedOfferingGroupMemberLoading = "rejected";
      })
      .addCase(addedOfferingGroupMember.fulfilled, (state, action) => {
        state.addedOfferingGroupMemberLoading = "fulfilled";
      })
      .addCase(updateOfferingGroupMemberCloseDate.pending, (state) => {
        state.updateOfferingGroupMemberCloseDateLoading = "pending";
      })
      .addCase(updateOfferingGroupMemberCloseDate.rejected, (state) => {
        state.updateOfferingGroupMemberCloseDateLoading = "rejected";
      })
      .addCase(updateOfferingGroupMemberCloseDate.fulfilled, (state, action) => {
        state.updateOfferingGroupMemberCloseDateLoading = "fulfilled";
      })
      .addCase(updateOfferingGroup.pending, (state) => {
        state.updateOfferingGroupLoading = "pending";
      })
      .addCase(updateOfferingGroup.rejected, (state) => {
        state.updateOfferingGroupLoading = "rejected";
      })
      .addCase(updateOfferingGroup.fulfilled, (state, action) => {
        state.updateOfferingGroupLoading = "fulfilled";
      })
      .addCase(updateOfferingGroupCloseDate.pending, (state) => {
        state.updateOfferingGroupCloseDateLoading = "pending";
      })
      .addCase(updateOfferingGroupCloseDate.rejected, (state) => {
        state.updateOfferingGroupCloseDateLoading = "rejected";
      })
      .addCase(updateOfferingGroupCloseDate.fulfilled, (state, action) => {
        state.updateOfferingGroupCloseDateLoading = "fulfilled";
      })
      .addCase(createOfferingGroup.pending, (state) => {
        state.createOfferingGroupLoading = "pending";
      })
      .addCase(createOfferingGroup.rejected, (state) => {
        state.createOfferingGroupLoading = "rejected";
      })
      .addCase(createOfferingGroup.fulfilled, (state, action) => {
        state.createOfferingGroupLoading = "fulfilled";
      })
      .addCase(searchOfferingGroupsAddOffer.pending, (state) => {
        state.offeringGroupsAddOfferLoading = "pending";
      })
      .addCase(searchOfferingGroupsAddOffer.rejected, (state) => {
        state.offeringGroupsAddOfferLoading = "rejected";
        state.offeringGroupsAddOffer = [];
      })
      .addCase(searchOfferingGroupsAddOffer.fulfilled, (state, { payload }) => {
        state.offeringGroupsAddOfferLoading = "fulfilled";
        state.offeringGroupsAddOffer = payload.content;
      })
      .addCase(addOfferingForGroup.pending, (state) => {
        state.addOfferingForGroupLoading = "pending";
      })
      .addCase(addOfferingForGroup.rejected, (state) => {
        state.addOfferingForGroupLoading = "rejected";
      })
      .addCase(addOfferingForGroup.fulfilled, (state, action) => {
        state.addOfferingForGroupLoading = "fulfilled";
      });
  },
});

export const {
  reducer: offeringGroupsReducer,
  actions: offeringGroupsActions,
} = offeringGroupsSlice;
